import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { notificationsManagementService, Notification, ListNotificationsParams, SendNotificationRequest } from '../services/notificationsManagement';
import './NotificationsPage.css';
import { Table, TableColumn } from '../components/Table';

export const NotificationsPage: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState<string>('all');
  const [readFilter, setReadFilter] = useState<string>('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [, setTotalCount] = useState(0);
  const [lastEvaluatedKey, setLastEvaluatedKey] = useState<string | undefined>();
  const [viewingNotification, setViewingNotification] = useState<string | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const itemsPerPage = 50;

  const loadNotifications = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params: ListNotificationsParams = {
        limit: itemsPerPage,
        search: searchTerm || undefined,
        type: typeFilter !== 'all' ? typeFilter : undefined,
        read: readFilter !== 'all' ? readFilter === 'true' : undefined,
      };

      if (currentPage > 1 && lastEvaluatedKey) {
        params.lastEvaluatedKey = lastEvaluatedKey;
      }

      const response = await notificationsManagementService.listNotifications(params);
      setNotifications(response.notifications);
      setTotalCount(response.count);
      setLastEvaluatedKey(response.lastEvaluatedKey);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load notifications');
      console.error('Error loading notifications:', err);
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- lastEvaluatedKey intentionally omitted
  }, [currentPage, typeFilter, readFilter, searchTerm]);

  useEffect(() => {
    loadNotifications();
  }, [loadNotifications]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
    loadNotifications();
  };

  const handleClearFilters = () => {
    setSearchTerm('');
    setTypeFilter('all');
    setReadFilter('all');
    setCurrentPage(1);
    setLastEvaluatedKey(undefined);
  };

  const handleExport = () => {
    const csvContent = [
      ['Notification ID', 'User ID', 'Type', 'Title', 'Read', 'Created'].join(','),
      ...notifications.map(n => [
        n.notificationId,
        n.userId,
        n.type,
        n.title,
        n.read ? 'Yes' : 'No',
        n.createdAt,
      ].join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `notifications-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const stats = useMemo(() => {
    const total = notifications.length;
    const read = notifications.filter(n => n.read).length;
    const unread = total - read;
    return { total, read, unread };
  }, [notifications]);

  const columns: TableColumn<Notification>[] = useMemo(() => [
    {
      header: 'Notification ID',
      accessor: 'notificationId',
      render: (value) => <div className="notification-id">{(value as string).substring(0, 8)}...</div>,
    },
    {
      header: 'User ID',
      accessor: 'userId',
      render: (value) => <div className="secondary-text">{(value as string).substring(0, 8)}...</div>,
      hideOnTablet: true,
    },
    {
      header: 'Type',
      accessor: 'type',
      render: (value) => <span className="type-badge">{value as string}</span>,
    },
    {
      header: 'Title',
      accessor: 'title',
      render: (value) => <div className="title-text">{value as string}</div>,
    },
    {
      header: 'Read',
      accessor: 'read',
      render: (value) => (
        <span className={value ? 'read-badge' : 'unread-badge'}>
          {value ? 'Read' : 'Unread'}
        </span>
      ),
    },
    {
      header: 'Created',
      accessor: 'createdAt',
      render: (value) => formatDate(value as string),
    },
    {
      header: 'Actions',
      accessor: 'notificationId',
      render: (value) => (
        <button
          onClick={() => setViewingNotification(value as string)}
          className="view-button"
        >
          View
        </button>
      ),
    },
  ], []);

  return (
    <div className="notifications-page">
      <div className="page-header">
        <h1>Notification Management</h1>
        <div className="header-actions">
          <div className="stats-summary">
            <div className="stat-item">
              <span className="stat-label">Total:</span>
              <span className="stat-value">{stats.total}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Read:</span>
              <span className="stat-value">{stats.read}</span>
            </div>
            <div className="stat-item">
              <span className="stat-label">Unread:</span>
              <span className="stat-value">{stats.unread}</span>
            </div>
          </div>
          <button onClick={() => setShowSendModal(true)} className="send-button">
            Send Notification
          </button>
          <button onClick={handleExport} className="export-button">
            Export CSV
          </button>
        </div>
      </div>

      <div className="notifications-filters">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by title or message..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="search-input"
          />
          <button type="submit" className="search-button">Search</button>
        </form>

        <div className="advanced-filters">
          <select
            value={typeFilter}
            onChange={(e) => {
              setTypeFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-select"
          >
            <option value="all">All Types</option>
            <option value="booking_created">Booking Created</option>
            <option value="booking_confirmed">Booking Confirmed</option>
            <option value="booking_cancelled">Booking Cancelled</option>
            <option value="payment_completed">Payment Completed</option>
            <option value="payment_failed">Payment Failed</option>
          </select>

          <select
            value={readFilter}
            onChange={(e) => {
              setReadFilter(e.target.value);
              setCurrentPage(1);
              setLastEvaluatedKey(undefined);
            }}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="true">Read</option>
            <option value="false">Unread</option>
          </select>

          <button onClick={handleClearFilters} className="clear-filters-button">
            Clear Filters
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      {loading ? (
        <div className="loading">Loading notifications...</div>
      ) : (
        <>
          <Table
            columns={columns}
            data={notifications}
            getRowId={(row) => row.notificationId}
            emptyMessage="No notifications found"
            containerClassName="notifications-table-container"
            className="notifications-table"
          />

          <div className="pagination">
            <button
              disabled={currentPage === 1}
              onClick={() => {
                setCurrentPage(currentPage - 1);
                setLastEvaluatedKey(undefined);
              }}
            >
              Previous
            </button>
            <span>Page {currentPage}</span>
            <button
              disabled={!lastEvaluatedKey || notifications.length < itemsPerPage}
              onClick={() => setCurrentPage(currentPage + 1)}
            >
              Next
            </button>
          </div>
        </>
      )}

      {viewingNotification && (
        <NotificationDetailModal
          notificationId={viewingNotification}
          onClose={() => {
            setViewingNotification(null);
            loadNotifications();
          }}
        />
      )}

      {showSendModal && (
        <SendNotificationModal
          onClose={() => {
            setShowSendModal(false);
            loadNotifications();
          }}
        />
      )}
    </div>
  );
};

const NotificationDetailModal: React.FC<{ notificationId: string; onClose: () => void }> = ({ notificationId, onClose }) => {
  const [notification, setNotification] = useState<Notification | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadNotification();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load on notificationId change only
  }, [notificationId]);

  const loadNotification = async () => {
    try {
      setLoading(true);
      const data = await notificationsManagementService.getNotification(notificationId);
      setNotification(data);
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to load notification');
      onClose();
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="modal-overlay" onClick={onClose}>
        <div className="modal-content" onClick={(e) => e.stopPropagation()}>
          <div className="loading">Loading notification...</div>
        </div>
      </div>
    );
  }

  if (!notification) {
    return null;
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content notification-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Notification Details</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        <div className="modal-body">
          <div className="detail-section">
            <label>Notification ID:</label>
            <div>{notification.notificationId}</div>
          </div>
          <div className="detail-section">
            <label>User ID:</label>
            <div>{notification.userId}</div>
          </div>
          <div className="detail-section">
            <label>Type:</label>
            <div>{notification.type}</div>
          </div>
          <div className="detail-section">
            <label>Title:</label>
            <div>{notification.title}</div>
          </div>
          <div className="detail-section">
            <label>Message:</label>
            <div className="message-text">{notification.message}</div>
          </div>
          {notification.relatedId && (
            <div className="detail-section">
              <label>Related ID:</label>
              <div>{notification.relatedId}</div>
            </div>
          )}
          {notification.relatedType && (
            <div className="detail-section">
              <label>Related Type:</label>
              <div>{notification.relatedType}</div>
            </div>
          )}
          <div className="detail-section">
            <label>Read:</label>
            <div>
              <span className={notification.read ? 'read-badge' : 'unread-badge'}>
                {notification.read ? 'Read' : 'Unread'}
              </span>
            </div>
          </div>
          <div className="detail-section">
            <label>Created:</label>
            <div>{new Date(notification.createdAt).toLocaleString()}</div>
          </div>
        </div>
      </div>
    </div>
  );
};

const SendNotificationModal: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const [sending, setSending] = useState(false);
  const [formData, setFormData] = useState<SendNotificationRequest>({
    userId: '',
    type: 'booking_created',
    title: '',
    message: '',
    relatedId: '',
    relatedType: undefined,
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.userId || !formData.title || !formData.message) {
      alert('Please fill in all required fields');
      return;
    }

    try {
      setSending(true);
      await notificationsManagementService.sendNotification({
        ...formData,
        relatedId: formData.relatedId || undefined,
        relatedType: formData.relatedType || undefined,
      });
      alert('Notification sent successfully');
      onClose();
    } catch (err: unknown) {
      alert(err instanceof Error ? err.message : 'Failed to send notification');
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content send-notification-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>Send Notification</h2>
          <button onClick={onClose} className="close-button">×</button>
        </div>
        <form onSubmit={handleSubmit} className="send-notification-form">
          <div className="form-group">
            <label>User ID *</label>
            <input
              type="text"
              value={formData.userId}
              onChange={(e) => setFormData({ ...formData, userId: e.target.value })}
              required
              className="input-field"
            />
          </div>
          <div className="form-group">
            <label>Type *</label>
            <select
              value={formData.type}
              onChange={(e) => setFormData({ ...formData, type: e.target.value })}
              required
              className="input-field"
            >
              <option value="booking_created">Booking Created</option>
              <option value="booking_confirmed">Booking Confirmed</option>
              <option value="booking_cancelled">Booking Cancelled</option>
              <option value="payment_completed">Payment Completed</option>
              <option value="payment_failed">Payment Failed</option>
            </select>
          </div>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              className="input-field"
            />
          </div>
          <div className="form-group">
            <label>Message *</label>
            <textarea
              value={formData.message}
              onChange={(e) => setFormData({ ...formData, message: e.target.value })}
              required
              rows={4}
              className="textarea-field"
            />
          </div>
          <div className="form-group">
            <label>Related ID (optional)</label>
            <input
              type="text"
              value={formData.relatedId}
              onChange={(e) => setFormData({ ...formData, relatedId: e.target.value })}
              className="input-field"
            />
          </div>
          <div className="form-group">
            <label>Related Type (optional)</label>
            <select
              value={formData.relatedType || ''}
              onChange={(e) => {
                const raw = e.target.value || undefined;
                const relatedType = (raw === 'booking' || raw === 'negotiation' || raw === 'payment') ? raw : undefined;
                setFormData({ ...formData, relatedType });
              }}
              className="input-field"
            >
              <option value="">None</option>
              <option value="booking">Booking</option>
              <option value="negotiation">Negotiation</option>
              <option value="payment">Payment</option>
            </select>
          </div>
          <div className="modal-actions">
            <button type="submit" disabled={sending} className="send-button">
              {sending ? 'Sending...' : 'Send Notification'}
            </button>
            <button type="button" onClick={onClose} className="cancel-button">Cancel</button>
          </div>
        </form>
      </div>
    </div>
  );
};
